/* ============================================================================
 * Brand Casa — Dual UK/US size label (GemPages surface only)
 * ----------------------------------------------------------------------------
 * GemPages renders its own Liquid sections that bypass Prestige's swatch.liquid,
 * so the native {% render 'bc-size-dual' %} call never fires on those PDPs.
 * This file augments GemPages-rendered size buttons post-render using the
 * SAME markup + CSS classes as the Liquid snippet, so visual output is
 * identical across surfaces.
 *
 * Config is read from window.__bcSizeDualConfig (populated by theme.liquid
 * from the same theme settings as the Liquid snippet — one source of truth).
 *
 * Scope:
 *   - Runs only on /products/* pages.
 *   - Only touches .option-value containers whose label text is exactly
 *     "UK Size N". Nothing else is queried.
 *   - MutationObserver is scoped to the product form / main, not the whole body.
 * ============================================================================
 */
(function () {
  'use strict';

  var isProductPage = location.pathname.indexOf('/products/') !== -1;
  var isCartPage = location.pathname.indexOf('/cart') !== -1 || location.pathname === '/cart';
  // Also run on any page where a cart drawer may open.
  // (No early return — we gate individual scans below by route.)

  var cfg = window.__bcSizeDualConfig || { enabled: true, prefix: 'US Size' };
  if (!cfg.enabled) return;

  var UK_RE = /^\s*UK\s*Size\s*(\d{1,2})\s*$/i;
  var MIN_UK = 4;
  var MAX_UK = 24;
  var PROCESSED = 'data-bc-size-processed';

  function ukToUs(uk) {
    var n = parseInt(uk, 10);
    if (isNaN(n) || n < MIN_UK || n > MAX_UK) return null;
    return n - 4;
  }

  /**
   * Replace the contents of a label span with our dual-label markup,
   * keeping the UK text intact (just re-wrapped) and appending the US line.
   * Mirrors the structure emitted by snippets/bc-size-dual.liquid.
   */
  function wrapLabel(labelSpan, uk) {
    var us = ukToUs(uk);
    if (us === null) return false;

    var original = (labelSpan.textContent || '').trim();

    var wrap = document.createElement('span');
    wrap.className = 'bc-size-dual';
    wrap.setAttribute('data-bc-size', '');

    var ukEl = document.createElement('span');
    ukEl.className = 'bc-size-dual__uk';
    ukEl.textContent = original;

    var usEl = document.createElement('span');
    usEl.className = 'bc-size-dual__us';
    usEl.setAttribute('aria-hidden', 'true');
    usEl.textContent = cfg.prefix + ' ' + us;

    wrap.appendChild(ukEl);
    wrap.appendChild(usEl);

    labelSpan.innerHTML = '';
    labelSpan.appendChild(wrap);
    return true;
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var containers = root.querySelectorAll('.option-value');
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      if (c.hasAttribute(PROCESSED)) continue;
      var labelSpan = c.querySelector('.gp-text-center, span');
      if (!labelSpan) continue;
      var m = UK_RE.exec((labelSpan.textContent || '').trim());
      if (!m) continue;
      if (wrapLabel(labelSpan, m[1])) {
        c.setAttribute(PROCESSED, '1');
        // Tag the parent clickable wrapper so CSS can target its border/padding
        // without needing :has() (broader browser compat, simpler specificity).
        var wrapper = c.closest('.option-value-wrapper');
        if (wrapper) wrapper.classList.add('bc-size-processed');
      }
    }
  }

  /**
   * Cart-surface enhancer: cart line items typically render variant.title
   * as "Color / UK Size N". Find the "UK Size N" substring and append "US Size M"
   * as a small sibling span. Covers cart page, cart drawer, order status.
   */
  var CART_SIZE_RE = /\bUK\s*Size\s*(\d{1,2})\b/i;
  function scanCart(root) {
    if (!root || !root.querySelectorAll) return;
    // Prestige's line-item shows variant.title in <p class="smallcaps text-subdued">
    // GemPages / other themes use various selectors — we match broadly but stay
    // within elements whose TEXT starts/contains only variant-option data.
    var selectors = [
      '.line-item__options',
      '.line-item__info p',
      '.cart-item__title + *',
      '[class*="line-item"] .smallcaps',
      '[class*="line-item"] [class*="variant"]',
      '[class*="cart"] [class*="variant-title"]',
      '[data-cart-item] p',
      'dd.line-item-property__value'
    ];
    var els = root.querySelectorAll(selectors.join(','));
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.hasAttribute('data-bc-cart-size')) continue;
      var text = (el.textContent || '').trim();
      var m = CART_SIZE_RE.exec(text);
      if (!m) continue;
      var uk = parseInt(m[1], 10);
      var us = ukToUs(uk);
      if (us === null) continue;
      // Append the US label inline after existing text
      var span = document.createElement('span');
      span.className = 'bc-cart-us-size';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = ' / ' + cfg.prefix + ' ' + us;
      el.appendChild(span);
      el.setAttribute('data-bc-cart-size', '1');
    }
  }

  function ukToUs(uk) {
    var n = parseInt(uk, 10);
    if (isNaN(n) || n < 4 || n > 24) return null;
    return n - 4;
  }

  function init() {
    var productRoot =
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('main') ||
      document.body;

    if (isProductPage) scan(productRoot);
    scanCart(document.body); // cart drawer can appear on any page

    if (typeof MutationObserver === 'undefined') return;
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) {
            if (isProductPage) scan(added[j]);
            scanCart(added[j]);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /** Inject a "Size Guide" link next to the "Size" option label on PDPs. */
  function injectSizeGuideLink() {
    if (!cfg.sizeGuideEnabled) return;
    var url = cfg.sizeGuideUrl || '/pages/size-guide';
    // Find the "Size" label in the product form. GemPages uses a plain DIV with text "Size".
    var candidates = document.querySelectorAll('.variant-inside > :first-child, legend, .product__option-label, label');
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (c.hasAttribute('data-bc-sizeguide-done')) continue;
      var t = (c.textContent || '').trim();
      if (t !== 'Size' && !/^Size\s*:?\s*$/i.test(t)) continue;
      // Only on elements inside the product form
      if (!c.closest('form[action*="/cart/add"], .variant-inside, .product-info, [data-product]')) continue;
      var link = document.createElement('a');
      link.className = 'bc-size-guide-link';
      link.textContent = 'Size Guide';
      link.href = url;
      link.setAttribute('aria-label', 'Open size guide');
      // Wrap existing label + new link in a flex row
      var row = document.createElement('div');
      row.className = 'bc-size-guide-row';
      c.parentNode.insertBefore(row, c);
      row.appendChild(c);
      row.appendChild(link);
      c.setAttribute('data-bc-sizeguide-done', '1');
      break;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); injectSizeGuideLink(); });
  } else {
    init();
    injectSizeGuideLink();
  }
})();
